import { useEffect, useRef, useState } from 'react';
import type { TaskKind } from '../types';

// Shared between TracePipeline's section headers and HopCard's collapsed
// one-liner — kept here (not in either component) so neither has to import
// the other just for this lookup table.
export const KIND_LABEL: Record<TaskKind, string> = {
  classify: 'classify',
  extract_fields: 'extract fields',
  summarize: 'summarize',
  normalize: 'normalize',
};

function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}

/** Cubic-ease count-up on rAF — SPEC.md §6 animation inventory (cost counter). */
export function useCountUp(target: number, durationMs = 650): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return;
    let start: number | null = null;

    const tick = (now: number) => {
      if (start === null) start = now;
      const elapsed = now - start;
      const p = Math.min(1, elapsed / durationMs);
      const eased = easeOutCubic(p);
      setValue(from + (target - from) * eased);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return value;
}

export function formatUsd(n: number): string {
  if (n === 0) return '$0.00';
  if (Math.abs(n) < 0.01) return `$${n.toFixed(6)}`;
  // Cent-rounding here hid the real gap on the benchmark bars: "$0.02"
  // frontier next to "$0.008119" router reads as ~2x when it's ~4.6x.
  if (Math.abs(n) < 1) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

export function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function formatPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

export function formatDownloads(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

export function timeAgo(ts: number): string {
  const diffMs = Date.now() - ts;
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
